/**
 * TreeView  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useRef } from "react";

import { TreeProps as RcTreeProps } from "rc-tree";
import {
	ITreeNode,
	ITreeState,
	ITreeViewAllowDrop,
	TREE_VIEW_STYLE,
	TTreeViewNodeActionClickHandler,
	TTreeViewNodeActionMenuItemClickHandler,
	TTreeViewNodeClickHandler,
	TTreeViewNodeDragDropHandler,
	TTreeViewNodeDragStartHandler,
	TTreeViewNodeFlagClickHandler,
	TTreeViewNodeHoverHandler,
	TTreeViewNodeSelectHandler
} from "./TreeViewTypes";

import { BEM } from "@adapptio/lib-components";
import { isBoolean, isFunction, isUndefined } from "@adapptio/lib-shared";

import { TreeNodeTitle } from "./TreeNodeTitle";
import { TreeSwitcherIcon } from "./TreeSwitcherIcon";
import { ITreeBaseProps, TreeBase } from "./TreeBase";
import { TreeNodeIcon } from "./TreeNodeIcon";
import { TreeDropIndicator } from "./TreeDropIndicator";
import { ICON_SIZE } from "../Icon";

type RcTreeDataItem = RcTreeProps["treeData"][0];

export interface ITreeViewProps {
	items: ITreeNode[];
	state: ITreeState;
	selections?: ITreeNode["id"][];
	multiselect?: boolean;
	className?: string;
	style?: TREE_VIEW_STYLE;
	isDraggable?: boolean | ((node: ITreeNode) => boolean);
	compact?: boolean;
	onNodeSelect?: TTreeViewNodeSelectHandler;
	onNodeDragStart?: TTreeViewNodeDragStartHandler;
	onNodeDragDrop?: TTreeViewNodeDragDropHandler;
	onNodeClick?: TTreeViewNodeClickHandler;
	onNodeFlagClick?: TTreeViewNodeFlagClickHandler;
	onNodeHover?: TTreeViewNodeHoverHandler;
	onNodeActionClick?: TTreeViewNodeActionClickHandler;
	onNodeActionMenuItemClick?: TTreeViewNodeActionMenuItemClickHandler;
	onStateUpdate: (state: ITreeState) => void;
	loadData?: (node: ITreeNode) => Promise<void>;
	getNodeClassName?: (node: ITreeNode) => string;
	renderDropIndicator?: ITreeBaseProps["dropIndicatorRender"];
	renderNodeTitle?: (props: { node: ITreeNode; titleComponent: React.ReactNode }) => React.ReactNode;
	switcherIconSize?: ICON_SIZE;
	defaultExpandAll?: ITreeBaseProps["defaultExpandAll"];
	defaultLevelsExpanded?: number;
	allowDrop?: ITreeViewAllowDrop;
	sort?: boolean;
	onNodeContextMenu?: TTreeViewNodeClickHandler;
}

const parentIdKey = Symbol("parent");

export const TreeView: React.FC<ITreeViewProps> = ({
	items,
	state,
	multiselect,
	selections,
	isDraggable,
	compact,
	onNodeClick,
	onNodeSelect,
	onNodeHover,
	onStateUpdate,
	onNodeFlagClick,
	onNodeActionClick,
	onNodeActionMenuItemClick,
	onNodeDragStart,
	onNodeDragDrop,
	className,
	style,
	loadData,
	getNodeClassName,
	renderDropIndicator,
	renderNodeTitle,
	switcherIconSize,
	defaultExpandAll,
	defaultLevelsExpanded,
	allowDrop,
	sort,
	onNodeContextMenu
}) => {
	const prefixClass = "edt-tree";
	const baseClass = BEM(prefixClass, null, {
		style,
		compact
	});

	const SwitcherIcon = useMemo<ITreeBaseProps["switcherIcon"]>(
		() =>
			({ isLeaf }) =>
				(
					<TreeSwitcherIcon
						isLeaf={isLeaf}
						size={switcherIconSize ?? compact ? ICON_SIZE.SMALL : ICON_SIZE.MEDIUM}
					/>
				),
		[ compact, switcherIconSize ]
	);

	const draggedNodeRef = useRef<ITreeNode | null>(null);

	const treeNodesMapRef = React.useRef(
		new Map<ITreeNode["id"], ITreeNode & { [key in typeof parentIdKey]?: ITreeNode["id"] }>()
	);

	const getTreeNodeClassName = useCallback<(node: ITreeNode) => string>((node) => {
		const hasChildren = node.children?.length > 0;
		const isParent = isBoolean(node.isLeaf) ? !node.isLeaf : Array.isArray(node.children);

		const nodeClass = BEM(node.className || "edt-tree-treenode", null, {
			parent: isParent,
			"has-children": hasChildren,
			disabled: node.disabled
		});

		if (isFunction(getNodeClassName)) {
			nodeClass.add(getNodeClassName(node));
		}

		return nodeClass.toClassName();
	}, [ getNodeClassName ]);

	const mapTreeNodesToTreeData = React.useCallback(
		(
			treeNodes: ITreeNode[],
			{ parentId }: { parentId?: ITreeNode["id"] } = {},
			nestingLevel = 0
		): RcTreeDataItem[] => {
			const treeNodesArr = [ ...treeNodes.filter((item) => item !== undefined) ];

			if (sort) {
				treeNodesArr.sort((a, b) => a?.label?.localeCompare(b?.label));
			}

			return treeNodesArr.map((treeNode) => {
				const defaultExpanded = isBoolean(treeNode?.defaultExpanded)
					? treeNode.defaultExpanded
					: defaultExpandAll || defaultLevelsExpanded >= nestingLevel + 1;
				treeNodesMapRef.current.set(treeNode.id, {
					...treeNode,
					[parentIdKey]: parentId,
					defaultExpanded
				});

				const isLeaf = isBoolean(treeNode.isLeaf)
					? treeNode.isLeaf
					: !treeNode.children?.length && !treeNode.isLoadable;

				const titleComponent = (
					<TreeNodeTitle
						node={treeNode}
						onFlagClick={onNodeFlagClick}
						onActionClick={onNodeActionClick}
						onActionMenuItemClick={onNodeActionMenuItemClick}
						getNodeClassName={getNodeClassName}
					/>
				);

				return {
					key: treeNode.id,
					title: isFunction(renderNodeTitle)
						? renderNodeTitle({ node: treeNode, titleComponent })
						: titleComponent,
					isLeaf,
					className: getTreeNodeClassName(treeNode),
					children:
						treeNode.children?.length > 0
							? mapTreeNodesToTreeData(
								treeNode.children,
								{ parentId: treeNode.id },
								nestingLevel + 1
							)
							: undefined
				};
			});
		},
		[ getNodeClassName, renderNodeTitle, defaultLevelsExpanded, defaultExpandAll, sort ]
	);
	const treeData: RcTreeDataItem[] = React.useMemo(() => mapTreeNodesToTreeData(items), [ items ]);
	const expandedKeys = React.useMemo<ITreeNode["id"][]>(() => {
		const stateExpanded = Object.keys(state.nodes).filter((nodeId) => state.nodes[nodeId]?.expanded);
		const defaultExpanded = Array.from(treeNodesMapRef.current.values()).reduce(
			(acc, node) =>
				node.defaultExpanded && isUndefined(state.nodes[node.id]?.expanded)
					? [ ...acc, node.id ]
					: acc,
			[]
		);

		return [ ...stateExpanded, ...defaultExpanded ];
	}, [ state, treeNodesMapRef.current ]);

	const getParentNodeIds = useCallback(
		(nodeId: ITreeNode["id"], parentIds: ITreeNode["id"][] = []) => {
			const parentId =
				treeNodesMapRef.current.has(nodeId) && treeNodesMapRef.current.get(nodeId)[parentIdKey];
			return parentId ? getParentNodeIds(parentId, [ parentId, ...parentIds ]) : parentIds;
		},
		[ treeNodesMapRef ]
	);

	const updateNodesExpand: (expandData: { nodeId: ITreeNode["id"]; expanded: boolean }[]) => void =
		React.useCallback(
			(expandData) => {
				onStateUpdate({
					nodes: {
						...state.nodes,
						...expandData.reduce(
							(acc, { nodeId, expanded }) => ({
								...acc,
								[nodeId]: {
									...acc[nodeId],
									expanded
								}
							}),
							state.nodes
						)
					}
				});
			},
			[ onStateUpdate, state ]
		);

	const onExpand: RcTreeProps["onExpand"] = React.useCallback(
		(newExpandedKeys, { node, expanded }) => {
			updateNodesExpand([ { nodeId: node.key, expanded } ]);
		},
		[ updateNodesExpand ]
	);

	const selectNode: RcTreeProps["onSelect"] = React.useCallback(
		(selectedKeys, { selected, node, nativeEvent }) => {
			const treeNode = treeNodesMapRef.current.get(node.key);

			if (!treeNode.selectable || !isFunction(onNodeSelect)) {
				updateNodesExpand([ { nodeId: treeNode.id, expanded: !expandedKeys.includes(treeNode.id) } ]);
				return;
			}

			const isMultiselect = nativeEvent.ctrlKey || nativeEvent.metaKey;

			onNodeSelect(treeNode, { selected, isMultiselect });
		},
		[ getParentNodeIds, expandedKeys, updateNodesExpand, onNodeSelect ]
	);

	const iconRenderer: ITreeBaseProps["icon"] = React.useCallback(
		(treeNodeProps) => <TreeNodeIcon node={treeNodesMapRef.current.get(treeNodeProps.data.key)} />,
		[ treeNodesMapRef ]
	);

	/** Drag n Drop  */
	const onDragStart = useCallback<ITreeBaseProps["onDragStart"]>(
		({ node, event }) => {
			const draggedNode = treeNodesMapRef.current.get(node.key);

			draggedNodeRef.current = draggedNode;

			if (isFunction(onNodeDragStart)) {
				onNodeDragStart({ draggedNode, event: event as React.DragEvent<HTMLDivElement> });
			}
		},
		[ onNodeDragStart ]
	);

	const onDrop: ITreeBaseProps["onDrop"] = useCallback(
		(info) => {
			draggedNodeRef.current = null;
			const draggedNode = treeNodesMapRef.current.get(info.dragNode.key);
			const dropTargetNode = treeNodesMapRef.current.get(info.node.key);
			const dropTargetParentNode = treeNodesMapRef.current.get(dropTargetNode[parentIdKey]);

			const [ , ...dropPositionPath ] = info.node.pos
				.split("-")
				.map((nodeIndex) => parseInt(nodeIndex));
			const dropPosition = info.dropPosition - dropPositionPath[dropPositionPath.length - 1];

			const [ , ...draggedNodePositionPath ] = info.dragNode.pos
				.split("-")
				.map((nodeIndex) => parseInt(nodeIndex));

			if (isFunction(onNodeDragDrop)) {
				onNodeDragDrop({
					draggedNode,
					draggedNodePositionPath,
					dropTargetNode,
					dropTargetParentNode,
					dropPosition,
					dropPositionPath,
					event: info.event
				});
			}
		},
		[ onNodeDragDrop ]
	);

	const handleDraggable = useCallback<(node: RcTreeDataItem) => boolean>(
		(node) => {
			if (isFunction(isDraggable)) {
				const treeNode = treeNodesMapRef.current.get(node.key);

				return isDraggable(treeNode);
			}

			return !!isDraggable;
		},
	[ isDraggable ]
	);

	const handleAllowDrop = useCallback<ITreeBaseProps["allowDrop"]>(
		({ dropNode, dropPosition }) => {
			if (isFunction(allowDrop)) {
				const dropTargetNode = treeNodesMapRef.current.get(dropNode.key);
				const draggedNode = draggedNodeRef.current;

				return allowDrop({ draggedNode, dropTargetNode, dropPosition });
			}

			return true;
		},
		[ allowDrop ]
	);

	const handleClick = useCallback<ITreeBaseProps["onClick"]>(
		(e, node) => {
			if (isFunction(onNodeClick)) {
				const treeNode = treeNodesMapRef.current.get(node?.key);
				onNodeClick(treeNode, e as React.MouseEvent<HTMLDivElement, MouseEvent>);
			}
		},
		[ onNodeClick ]
	);

	/** Automatically expand all parents of selected nodes */
	React.useEffect(() => {
		const keysToExpand = (selections || []).flatMap((selectionId) => [
			...getParentNodeIds(selectionId),
			selectionId
		]);

		updateNodesExpand(keysToExpand.map((nodeId) => ({ nodeId, expanded: true })));
	}, [ selections, getParentNodeIds ]);

	const handleLoadData = useCallback<ITreeBaseProps["loadData"]>(
		(treeNode) => {
			if (isFunction(loadData)) {
				const node = treeNodesMapRef.current.get(treeNode.key);
				return loadData(node);
			}

			return Promise.resolve();
		},
		[ loadData ]
	);

	const handleNodeMouseEnter = useCallback<ITreeBaseProps["onMouseEnter"]>(
		({ node: treeNode }) => {
			if (isFunction(onNodeHover)) {
				const node = treeNodesMapRef.current.get(treeNode.key);
				return onNodeHover(node, { hovered: true });
			}
		},
		[ onNodeHover ]
	);

	const handleNodeMouseLeave = useCallback<ITreeBaseProps["onMouseLeave"]>(
		({ node: treeNode }) => {
			if (isFunction(onNodeHover)) {
				const node = treeNodesMapRef.current.get(treeNode.key);
				return onNodeHover(node, { hovered: false });
			}
		},
		[ onNodeHover ]
	);

	const handleContextMenu = useCallback<ITreeBaseProps["onRightClick"]>(
		({ event, node: treeNode }) => {
			if (isFunction(onNodeContextMenu)) {
				const node = treeNodesMapRef.current.get(treeNode.key);
				return onNodeContextMenu(node, event as React.MouseEvent<HTMLDivElement, MouseEvent>);
			}
		},
		[ onNodeContextMenu ]
	);

	if (className) {
		baseClass.add(className);
	}

	const treeProps: ITreeBaseProps = {
		allowDrop: handleAllowDrop,
		className: baseClass.toClassName(),
		draggable: handleDraggable,
		dropIndicatorRender: renderDropIndicator,
		icon: iconRenderer,
		loadData: handleLoadData,
		multiple: multiselect,
		onClick: handleClick,
		onDragStart,
		onDrop,
		onMouseEnter: handleNodeMouseEnter,
		onMouseLeave: handleNodeMouseLeave,
		onRightClick: isFunction(onNodeContextMenu) ? handleContextMenu : onNodeContextMenu,
		onSelect: selectNode,
		prefixCls: prefixClass,
		selectedKeys: selections,
		showIcon: true,
		switcherIcon: SwitcherIcon,
		treeData,
		virtual: true
	};

	treeProps.expandedKeys = expandedKeys;
	treeProps.onExpand = onExpand;

	return <TreeBase {...treeProps} />;
};

TreeView.defaultProps = {
	multiselect: false,
	style: TREE_VIEW_STYLE.DEFAULT,
	renderDropIndicator: TreeDropIndicator
};
